"use client";

import React, { createContext, useContext, useEffect } from "react";
import { LicenseName } from "@/data/license.data";
import { useCurrentUser } from "@/hooks/use-current-user";
import { signIn } from "next-auth/react";
import { Subscription } from "@prisma/client";
import { useQuery, useQueryClient } from "react-query";
import { LicenseResponse } from "@/app/api/license/route";
import { updateUserLastOnline } from "@/actions/auth/user";

export interface ProfileContextType {
  user: any;
  license: LicenseName;
  subscription: Subscription | null;
  isLoading: boolean;
  isLicenseFetched: boolean;
  clearProfileContext: () => void;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

export const useProfileContext = (needSignIn: boolean = false) => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  if (!context.user?.id && needSignIn) {
    signIn();
  }
  return context;
};

const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const user = useCurrentUser();

  const clearProfileContext = () => {
    queryClient.setQueryData(["userProfile", user?.id], {
      license: "Trial",
      subscription: null,
    });
  };

  const fetchLicense = async (userId?: string) => {
    let license: LicenseName;
    let subscription: Subscription | null = null;

    if (!userId) {
      license = "Trial";
    } else {
      const res = await fetch("/api/license");

      const licenseData = (await res.json()) as LicenseResponse;

      license = licenseData.license;

      subscription = licenseData.subscription
        ? {
            ...licenseData.subscription,
            boughtAt: new Date(licenseData.subscription?.boughtAt),
            cancelAt: licenseData.subscription.cancelAt
              ? new Date(licenseData.subscription?.cancelAt)
              : null,
          }
        : null;
    }

    return { license, subscription };
  };

  const { data, isLoading, isFetched } = useQuery(
    ["userProfile", user?.id],
    () => fetchLicense(user?.id),
    {
      onError: (err) => {
        console.error("Failed to fetch user license:", err);
      },
    },
  );

  useEffect(() => {
    if (user?.id) {
      updateUserLastOnline(user.id);
    }
  }, [user?.id]);

  return (
    <ProfileContext.Provider
      value={{
        user,
        license: data?.license ?? "Trial",
        subscription: data?.subscription ?? null,
        isLoading,
        isLicenseFetched: isFetched,
        clearProfileContext,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileProvider;
